import { capitalCase } from 'change-case';
import { TimeSpan, TimestampRange } from 'rewire-common';
import { DataSignal, observable, property } from 'rewire-core';
import { createColumn, createGrid, ICell, IColumn, IGrid, IGridOptions, IRowData } from 'rewire-grid';
import { INameEntity, IResponse, IResponseType, ITimecard, MessageLevel, ResponseFilter } from '../DataTypes';
import Queries from '../graphql/Queries';
import Mutations from '../graphql/Mutations';
import { DeleteIconRenderer } from '../timecard/CellRenderers';
import { notifications } from '../ui/Notifications';
import { confirmationModel } from '../ui/ConfirmationDialog';

const responsesReturnSchema = `
  id
  group {
    id
    name
  }
  units
  effective
  generator {
    id
    name
  }
  occupation {
    id
    name
  }
  template {
    id
    name
    template {
      start
      end
    }
  }
  employee {
    id
    name
  }
  type
  comment
  createdDate
  createdBy
`;

export interface IResponseInput {
  type: IResponseType;
  reasonId?: string;
  comment?: string;
}

const nameFn = (v: INameEntity) => v?.name || '';

function isHigherLevel(level: MessageLevel, newLevel: MessageLevel) {
  const levels: MessageLevel[] = ['info', 'warning', 'error'];
  return levels.indexOf(newLevel) > levels.indexOf(level);
}

function escalateLevel(level: MessageLevel, newLevel: MessageLevel) {
  return isHigherLevel(level, newLevel) ? newLevel : level;
}

export interface IResponseLevel {
  level: MessageLevel;
  message: string;
}

export class ResponsesLogViewModel {
  grid: IGrid;
  private _timecards?: ITimecard[];
  private _filter?: ResponseFilter;
  private _responseWithHighestLevel: DataSignal<IResponseLevel | undefined> = property(undefined);

  constructor() {
    this.grid = this.createResponsesGrid();
  }

  get responseWithHighestLevel(): IResponseLevel | undefined {
    return this._responseWithHighestLevel();
  }

  async deleteResponses(responses: IResponse[]) {
    const responseIds = responses.map((r) => r.id);
    const message = responses.map((r) => r.employee.name).join(', ');
    const isMultiple = responseIds.length > 1;
    confirmationModel.load(`Are you sure you want to delete ${isMultiple ? 'these responses' : 'this response'} for ${message}?`, {
      yesAction: async () => {
        if (await Mutations.deleteResponses(responseIds)) {
          notifications.success('Responses deleted');
          const rows = this.grid.rows.filter((r) => responseIds.includes(r.data.id)).map((r) => r.id);
          this.grid.removeRows(rows ?? []);
          return true;
        }

        notifications.error('Failed to delete responses');
        return false;
      },
      maxWidth: 'sm',
      title: `Confirm response${isMultiple ? 's' : ''} Deletion`,
    });
  }

  reset() {
    this.grid.set([]);
  }

  async refresh() {
    return this.loadResponse(this._timecards, this._filter!);
  }

  async loadResponse(timecards: ITimecard[] | undefined, filter: ResponseFilter) {
    this._timecards = timecards;
    this._filter = filter;
    const responses = (await Queries.responsesSearch(responsesReturnSchema, filter, 20)) as unknown as IResponse[];
    responses.sort((a, b) => (b.createdDate?.utc || 0) - (a.createdDate?.utc || 0));
    this.grid.set(responses.map((r) => ({ id: r.id, data: r }) as IRowData));

    let matchingResponseWithHighestLevel: IResponseLevel | undefined;
    for (const row of this.grid.rows) {
      const response = row.data;
      const matchResponse = this.getMatchResponse(response, timecards);
      if (!matchResponse) continue;
      if (isHigherLevel(matchingResponseWithHighestLevel?.level || 'info', matchResponse.level)) {
        matchingResponseWithHighestLevel = matchResponse;
      }
      row.data.matchResponse = matchResponse;
      row.cls = matchResponse ? `matches ${matchResponse.level}` : '';
    }

    this._responseWithHighestLevel(matchingResponseWithHighestLevel);
  }

  getMatchResponse(response: IResponse, timecards?: ITimecard[]): { level: MessageLevel; message: string } | undefined {
    if (!timecards || response.type === 'yes') return undefined;
    const responseMessage: string[] = [];
    let level: MessageLevel = 'info';
    const range = new TimestampRange(
      response.effective.add(response.template?.template?.[0]?.start ?? 0, TimeSpan.hours),
      response.effective.add(response.template?.template?.[0]?.end ?? 24, TimeSpan.hours),
    );

    if (response.type === 'no') {
      responseMessage.push('has indicated they should not be scheduled');
    }
    if (response.type === 'unavailable') {
      responseMessage.push('has not yet been able to be contacted');
    }
    for (const timecard of timecards) {
      if (!timecard.effective || !timecard.group || !timecard.occupation) continue;

      if (range.intersects(timecard.effective)) {
        responseMessage.push('overlaps with the shift');
        level = escalateLevel(level, 'warning');
      }

      if (timecard.group?.id === response.group?.id && timecard.occupation?.id === response.occupation?.id) {
        responseMessage.push('with the same group and occupation');
        if (response.type === 'no' && level === 'warning') {
          level = 'error';
          break;
        }
        level = escalateLevel(level, 'warning');
      }
      if (level === 'error') break;
    }
    if (responseMessage.length > 0) {
      return { level, message: `${response.employee?.name ?? 'the employee'} ${responseMessage.join(' and ')}.` };
    }
    return undefined;
  }

  createResponsesGrid() {
    const cols: IColumn[] = observable([]);

    cols.push(
      createColumn('hideDeleteIcon', '', {
        canSort: false,
        type: { type: 'none', options: { onClick: (cell: ICell) => () => this.deleteResponses([cell.row.data]) } },
        renderer: DeleteIconRenderer,
        width: '25px',
        align: 'center',
        visble: true,
        enabled: true,
      } as any),
    );

    cols.push(
      createColumn('employee', 'Employee', {
        width: '110px',
        canSort: false,
        visible: true,
        readOnly: true,
        map: nameFn,
      }),
    );
    cols.push(
      createColumn('type', 'Response', {
        width: '80px',
        canSort: false,
        visible: true,
        readOnly: true,
        map: (v) => (v && capitalCase(v)) || '',
      }),
    );
    cols.push(
      createColumn('comment', 'Comment', {
        width: '180px',
        canSort: false,
        visible: true,
        readOnly: true,
      }),
    );
    cols.push(
      createColumn('createdDate', 'Call Time', {
        width: '110px',
        canSort: false,
        visible: true,
        readOnly: true,
        map: (v) => v?.toTimestampString() || '',
      }),
    );
    cols.push(
      createColumn('createdBy', 'Called By', {
        width: '110px',
        canSort: false,
        visible: true,
        readOnly: true,
      }),
    );
    cols.push(
      createColumn('units', 'Units', {
        width: '60px',
        canSort: false,
        visible: true,
        type: { type: 'number', options: { decimals: 4, thousandSeparator: false, allowNegative: false } },
        readOnly: true,
        align: 'left',
      }),
    );
    cols.push(
      createColumn('group', 'Group', {
        width: '110px',
        canSort: false,
        visible: true,
        readOnly: true,
        map: nameFn,
      }),
    );
    cols.push(
      createColumn('occupation', 'Occupation', {
        width: '110px',
        canSort: false,
        visible: true,
        readOnly: true,
        map: nameFn,
      }),
    );
    cols.push(
      createColumn('generator', 'Shift Pattern', {
        width: '110px',
        canSort: false,
        visible: true,
        readOnly: true,
        map: nameFn,
      }),
    );
    cols.push(
      createColumn('template', 'Template', {
        width: '110px',
        canSort: false,
        visible: true,
        readOnly: true,
        map: nameFn,
      }),
    );

    const rows: IRowData[] = [];
    // create the grid model
    const gridOptions: IGridOptions = {
      // readOnly: true,
      headerRowHeight: 24,
      rowHeight: 31,
      rowKeybindPermissions: {
        insertRow: false,
        duplicateRow: false,
        deleteRow: false,
      },
      clearSelectionOnBlur: false,
    };

    const grid: IGrid = createGrid(rows, cols, gridOptions);
    return grid;
  }
}

const responsesLogViewModel = new ResponsesLogViewModel();
export default responsesLogViewModel;
